import { NgIf } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { EnrichedCartItem } from '@oe-backend/types';
import { ButtonComponent } from '@zeiss/beyond-online-angular';
import { BehaviorSubject } from 'rxjs';
import { LoadingAnimationService } from 'src/app/modules/loading-animation/loading-animation.service';
import { CartStore } from 'src/app/state-management/stores/cart.store';
import { ConfigurationStore } from 'src/app/state-management/stores/configuration.store';
import { OrderStore } from 'src/app/state-management/stores/order.store';
import {
	IconComponent,
	PopUpConfig,
	PopUpRef,
	PopUpService,
	ThreeSectionDrawerLayoutComponent,
} from 'vis-ods-angular';
import { LoadingAnimationDirective } from '../../../modules/loading-animation/loading-animation.directive';
import { RoutesEnum } from '../../../routing/routes.enum';
import { SummaryRoutesEnum } from '../../../routing/summary-outlet-routes.enum';
import { ProductSummaryComponent } from '../../../shared/components/product-summary/product-summary.component';
import { SingleOrderSubmitSuccessPopupComponent } from '../../../shared/components/single-order-submit-success-popup/single-order-submit-success-popup.component';
import { DateTimeFormatPipe } from '../../../shared/pipes/dateTime-format.pipe';
import { UserPermissionPipe } from '../../../shared/pipes/user-permission.pipe';
import { RolesEnum } from '../../../types/enums/roles-state.enum';

@Component({
	selector: 'app-shopping-cart-product-detail',
	templateUrl: './shopping-cart-product-detail.component.html',
	imports: [
		TranslocoModule,
		ThreeSectionDrawerLayoutComponent,
		LoadingAnimationDirective,
		NgIf,
		IconComponent,
		ProductSummaryComponent,
		ButtonComponent,
		UserPermissionPipe,
		DateTimeFormatPipe,
	],
})
export class ShoppingCartProductDetailComponent {
	private readonly orderStore = inject(OrderStore);
	private readonly cartStore = inject(CartStore);
	private readonly router = inject(Router);
	private readonly popUpService = inject(PopUpService);
	private readonly loadingAnimationService = inject(LoadingAnimationService);

	private readonly configurationStore = inject(ConfigurationStore);
	public readonly isBlockedFromOrdering = this.configurationStore.isBlockedFromOrdering;
	public readonly blockRemoveDate =
		this.configurationStore.settings()?.block_submit_order_remove_date;

	public popUpConfig = inject(PopUpConfig<{ detailData: EnrichedCartItem }>);
	public popUp = inject(PopUpRef);

	public order = this.orderStore.order;

	public readonly rolesEnum = RolesEnum;

	public $isLoading = this.orderStore.isLoading('submit');

	async backToProductConfig() {
		await this.router.navigateByUrl(`${RoutesEnum.order}/${this.popUpConfig?.data.detailData.id}`);
		this.popUp?.close({ doNotUnload: true });
	}

	openPrintDialog(): void {
		window.print();
	}

	async submitOrder() {
		this.loadingAnimationService.open();
		await this.orderStore.submitOrdersByIds([this.popUpConfig.data?.detailData.id]);
		if (this.orderStore.hasError('submit')()) return;

		await this.router.navigate([RoutesEnum.shoppingCart]);
		this.loadingAnimationService.close();
		this.popUp.close();
		window.scrollTo({
			top: 0,
			left: 0,
			behavior: 'smooth',
		});
		this.popUpService.open(SingleOrderSubmitSuccessPopupComponent, {
			enableCloseButton: new BehaviorSubject<boolean>(true),
			disableBackdropClose: new BehaviorSubject<boolean>(true),
		});
		this.cartStore.fetchCartItemCount();
		await this.cartStore.fetchCartItems();
	}

	removeProductFromShoppingCart() {
		this.popUp.close({ remove: true });
	}
	routeToShippingAddressSelector() {
		this.router.navigate(
			[{ outlets: { summary: [SummaryRoutesEnum.shoppingCartStoreSelector] } }],
			{
				state: { updateCartItemAfterChange: true },
			},
		);
	}
	routeToContactPersonSelector() {
		this.router.navigate([{ outlets: { summary: [SummaryRoutesEnum.contactPersonSelector] } }], {
			state: { updateCartItemAfterChange: true },
		});
	}
}
